import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityFormDialog } from '@/shared/components';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import type { V2Stream, DropdownOption, StreamFormData } from '../../types';
import { SENSOR_PRESETS } from '../../types';

const NONE_SENTINEL = '__none__';
const EMPTY_PRESET_SENTINEL = '__empty__';

interface StreamFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stream: V2Stream | null;
  options: {
    sensors: DropdownOption[];
    transmitters: DropdownOption[];
    organisations: DropdownOption[];
  } | null;
  onSubmit: (data: StreamFormData) => Promise<void>;
  isLoading: boolean;
}

function toNum(val: string): number {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function toNumOrNull(val: string): number | null {
  if (val === '') return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

export function StreamFormModal({
  open,
  onOpenChange,
  stream,
  options,
  onSubmit,
  isLoading,
}: StreamFormModalProps) {
  const { t } = useTranslation('streams');

  // Basic info
  const [name, setName] = useState('');
  const [probeSerial, setProbeSerial] = useState('');
  const [sensor, setSensor] = useState<string>(NONE_SENTINEL);
  const [transmitter, setTransmitter] = useState<string>(NONE_SENTINEL);
  const [organisation, setOrganisation] = useState('');
  const [description, setDescription] = useState('');

  // Calibration parameters
  const [offset, setOffset] = useState('0');
  const [uncertainty, setUncertainty] = useState('');
  const [roundingDecimals, setRoundingDecimals] = useState('');
  const [calibrationBatch, setCalibrationBatch] = useState('');

  // Polynomial
  const [polyX1, setPolyX1] = useState('1');
  const [polyX2, setPolyX2] = useState('0');
  const [polyX3, setPolyX3] = useState('0');
  const [polyX4, setPolyX4] = useState('0');
  const [polyX5, setPolyX5] = useState('0');

  // ADC
  const [adcThreshold, setAdcThreshold] = useState('');
  const [adcResolution, setAdcResolution] = useState('');
  const [adcResistance, setAdcResistance] = useState('');

  // Steinhart-Hart
  const [shA, setShA] = useState('');
  const [shB, setShB] = useState('');
  const [shC, setShC] = useState('');

  // Data filtering
  const [filterMin, setFilterMin] = useState('');
  const [filterMax, setFilterMax] = useState('');
  const [excludedValuesStr, setExcludedValuesStr] = useState('');

  // Reset form when open/stream changes
  useEffect(() => {
    if (!open) return;

    if (stream) {
      setName(stream.name ?? '');
      setProbeSerial(stream.probe_serial ?? '');
      setSensor(stream.sensor ?? NONE_SENTINEL);
      setTransmitter(stream.transmitter ?? NONE_SENTINEL);
      setOrganisation(stream.organisation ?? '');
      setDescription(stream.description ?? '');
      setOffset(String(stream.offset ?? 0));
      setUncertainty(stream.uncertainty != null ? String(stream.uncertainty) : '');
      setRoundingDecimals(stream.rounding_decimals != null ? String(stream.rounding_decimals) : '');
      setCalibrationBatch(stream.calibration_batch ?? '');
      setPolyX1(String(stream.polynomial?.x1 ?? 1));
      setPolyX2(String(stream.polynomial?.x2 ?? 0));
      setPolyX3(String(stream.polynomial?.x3 ?? 0));
      setPolyX4(String(stream.polynomial?.x4 ?? 0));
      setPolyX5(String(stream.polynomial?.x5 ?? 0));
      setAdcThreshold(stream.adc?.threshold != null ? String(stream.adc.threshold) : '');
      setAdcResolution(stream.adc?.resolution != null ? String(stream.adc.resolution) : '');
      setAdcResistance(stream.adc?.resistance != null ? String(stream.adc.resistance) : '');
      setShA(stream.steinharthart?.A != null ? String(stream.steinharthart.A) : '');
      setShB(stream.steinharthart?.B != null ? String(stream.steinharthart.B) : '');
      setShC(stream.steinharthart?.C != null ? String(stream.steinharthart.C) : '');
      setFilterMin(stream.filter?.min != null ? String(stream.filter.min) : '');
      setFilterMax(stream.filter?.max != null ? String(stream.filter.max) : '');
      setExcludedValuesStr(
        Array.isArray(stream.excluded_values) ? stream.excluded_values.join(', ') : ''
      );
    } else {
      // Create mode defaults
      setName('');
      setProbeSerial('');
      setSensor(NONE_SENTINEL);
      setTransmitter(NONE_SENTINEL);
      setOrganisation(options?.organisations?.[0]?._id ?? '');
      setDescription('');
      setOffset('0');
      setUncertainty('');
      setRoundingDecimals('');
      setCalibrationBatch('');
      setPolyX1('1');
      setPolyX2('0');
      setPolyX3('0');
      setPolyX4('0');
      setPolyX5('0');
      setAdcThreshold('');
      setAdcResolution('');
      setAdcResistance('');
      setShA('');
      setShB('');
      setShC('');
      setFilterMin('');
      setFilterMax('');
      setExcludedValuesStr('');
    }
  }, [open, stream, options]);

  const applyPreset = (key: string) => {
    if (key === EMPTY_PRESET_SENTINEL) {
      setAdcThreshold('');
      setAdcResolution('');
      setAdcResistance('');
      setShA('');
      setShB('');
      setShC('');
      return;
    }
    const preset = SENSOR_PRESETS[key];
    if (!preset) return;
    setAdcThreshold(String(preset.adc.threshold));
    setAdcResolution(String(preset.adc.resolution));
    setAdcResistance(String(preset.adc.resistance));
    setShA(String(preset.steinharthart.A));
    setShB(String(preset.steinharthart.B));
    setShC(String(preset.steinharthart.C));
  };

  const handleSubmit = async () => {
    const excludedValues = excludedValuesStr
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .map((s) => parseFloat(s))
      .filter((n) => !isNaN(n));

    const data: StreamFormData = {
      name: name.trim(),
      sensor: sensor === NONE_SENTINEL ? null : sensor,
      transmitter: transmitter === NONE_SENTINEL ? null : transmitter,
      organisation,
      probe_serial: probeSerial.trim() || null,
      description: description.trim(),
      offset: toNum(offset),
      uncertainty: toNumOrNull(uncertainty),
      rounding_decimals: toNumOrNull(roundingDecimals),
      calibration_batch: calibrationBatch.trim() || null,
      polynomial: {
        x1: toNum(polyX1),
        x2: toNum(polyX2),
        x3: toNum(polyX3),
        x4: toNum(polyX4),
        x5: toNum(polyX5),
      },
      adc: {
        threshold: toNumOrNull(adcThreshold),
        resolution: toNumOrNull(adcResolution),
        resistance: toNumOrNull(adcResistance),
      },
      steinharthart: {
        A: toNumOrNull(shA),
        B: toNumOrNull(shB),
        C: toNumOrNull(shC),
      },
      filter: {
        min: toNumOrNull(filterMin),
        max: toNumOrNull(filterMax),
      },
      excluded_values: excludedValues,
    };

    await onSubmit(data);
  };

  const isEditMode = stream !== null;
  const showOrgDropdown = (options?.organisations?.length ?? 0) > 1;

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="2xl"
      title={isEditMode ? t('modal.editTitle') : t('modal.createTitle')}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      saveDisabled={!name.trim()}
      saveLabel={isLoading ? t('modal.saving') : isEditMode ? t('modal.save') : t('modal.create')}
      cancelLabel={t('modal.cancel')}
    >
      <div className="space-y-6">
          {/* Section 1: Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t('modal.basicInfo')}
            </h3>

            <div className="grid gap-2">
              <Label htmlFor="stream-name">
                {t('modal.name')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="stream-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="stream-probe-serial">{t('modal.probeSerial')}</Label>
              <Input
                id="stream-probe-serial"
                value={probeSerial}
                onChange={(e) => setProbeSerial(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="stream-sensor">{t('modal.sensor')}</Label>
              <Select value={sensor} onValueChange={setSensor}>
                <SelectTrigger id="stream-sensor">
                  <SelectValue placeholder={t('modal.selectSensor')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_SENTINEL}>{t('modal.selectSensor')}</SelectItem>
                  {options?.sensors.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}{s.dev_id ? ` (${s.dev_id})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="stream-transmitter">{t('modal.transmitter')}</Label>
              <Select value={transmitter} onValueChange={setTransmitter}>
                <SelectTrigger id="stream-transmitter">
                  <SelectValue placeholder={t('modal.selectTransmitter')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_SENTINEL}>{t('modal.selectTransmitter')}</SelectItem>
                  {options?.transmitters.map((tx) => (
                    <SelectItem key={tx._id} value={tx._id}>
                      {tx.name}{tx.dev_id ? ` (${tx.dev_id})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showOrgDropdown && (
              <div className="grid gap-2">
                <Label htmlFor="stream-organisation">{t('modal.organisation')}</Label>
                <Select value={organisation} onValueChange={setOrganisation}>
                  <SelectTrigger id="stream-organisation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.organisations.map((org) => (
                      <SelectItem key={org._id} value={org._id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="stream-description">{t('modal.description')}</Label>
              <Textarea
                id="stream-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Section 2: Calibration Certificate (read-only, edit mode only) */}
          {isEditMode && stream?.calibration && (
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('modal.calibrationInfo')}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {stream.calibration.cert_number && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('modal.certNumber')}:</span>
                      <Badge variant="outline">{stream.calibration.cert_number}</Badge>
                    </div>
                  )}
                  {stream.calibration.date && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('modal.calibrationDate')}:</span>
                      <Badge variant="secondary">
                        {new Date(stream.calibration.date).toLocaleDateString()}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Section 3: Calibration Parameters */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t('modal.calibrationParams')}
            </h3>

            {/* Preset selector */}
            <div className="grid gap-2">
              <Label>{t('modal.selectPreset')}</Label>
              <Select onValueChange={applyPreset} defaultValue={EMPTY_PRESET_SENTINEL}>
                <SelectTrigger>
                  <SelectValue placeholder={t('modal.noPreset')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_PRESET_SENTINEL}>{t('modal.noPreset')}</SelectItem>
                  {Object.entries(SENSOR_PRESETS).map(([key, preset]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${preset.color}`} />
                        {preset.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="stream-offset">{t('modal.offset')}</Label>
                <Input
                  id="stream-offset"
                  type="number"
                  step="any"
                  value={offset}
                  onChange={(e) => setOffset(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="stream-uncertainty">{t('modal.uncertainty')}</Label>
                <Input
                  id="stream-uncertainty"
                  type="number"
                  step="any"
                  value={uncertainty}
                  onChange={(e) => setUncertainty(e.target.value)}
                  placeholder="—"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="stream-rounding">{t('modal.roundingDecimals')}</Label>
                <Input
                  id="stream-rounding"
                  type="number"
                  step="1"
                  value={roundingDecimals}
                  onChange={(e) => setRoundingDecimals(e.target.value)}
                  placeholder="—"
                />
              </div>
            </div>

            {/* Polynomial */}
            <div className="grid gap-2">
              <Label>{t('modal.polynomial')}</Label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { label: 'x1', value: polyX1, setter: setPolyX1 },
                  { label: 'x2', value: polyX2, setter: setPolyX2 },
                  { label: 'x3', value: polyX3, setter: setPolyX3 },
                  { label: 'x4', value: polyX4, setter: setPolyX4 },
                  { label: 'x5', value: polyX5, setter: setPolyX5 },
                ].map(({ label, value, setter }) => (
                  <div key={label} className="grid gap-1">
                    <Label className="text-xs text-muted-foreground">{label}</Label>
                    <Input
                      type="number"
                      step="any"
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* ADC */}
            <div className="grid gap-2">
              <Label>{t('modal.adc')}</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-1">
                  <Label className="text-xs text-muted-foreground">{t('modal.threshold')}</Label>
                  <Input
                    type="number"
                    step="any"
                    value={adcThreshold}
                    onChange={(e) => setAdcThreshold(e.target.value)}
                    placeholder="—"
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs text-muted-foreground">{t('modal.resolution')}</Label>
                  <Input
                    type="number"
                    step="any"
                    value={adcResolution}
                    onChange={(e) => setAdcResolution(e.target.value)}
                    placeholder="—"
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs text-muted-foreground">{t('modal.resistance')}</Label>
                  <Input
                    type="number"
                    step="any"
                    value={adcResistance}
                    onChange={(e) => setAdcResistance(e.target.value)}
                    placeholder="—"
                  />
                </div>
              </div>
            </div>

            {/* Steinhart-Hart */}
            <div className="grid gap-2">
              <Label>{t('modal.steinharthart')}</Label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'A', value: shA, setter: setShA },
                  { label: 'B', value: shB, setter: setShB },
                  { label: 'C', value: shC, setter: setShC },
                ].map(({ label, value, setter }) => (
                  <div key={label} className="grid gap-1">
                    <Label className="text-xs text-muted-foreground">{label}</Label>
                    <Input
                      type="number"
                      step="any"
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      placeholder="—"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="stream-calib-batch">{t('modal.calibrationBatch')}</Label>
              <Input
                id="stream-calib-batch"
                value={calibrationBatch}
                onChange={(e) => setCalibrationBatch(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Section 4: Data Filtering */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t('modal.dataFiltering')}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="stream-filter-min">{t('modal.filterMin')}</Label>
                <Input
                  id="stream-filter-min"
                  type="number"
                  step="any"
                  value={filterMin}
                  onChange={(e) => setFilterMin(e.target.value)}
                  placeholder="—"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="stream-filter-max">{t('modal.filterMax')}</Label>
                <Input
                  id="stream-filter-max"
                  type="number"
                  step="any"
                  value={filterMax}
                  onChange={(e) => setFilterMax(e.target.value)}
                  placeholder="—"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="stream-excluded-values">{t('modal.excludedValues')}</Label>
              <Input
                id="stream-excluded-values"
                value={excludedValuesStr}
                onChange={(e) => setExcludedValuesStr(e.target.value)}
                placeholder="e.g. -999, 0, 9999"
              />
              <p className="text-xs text-muted-foreground">{t('modal.excludedValuesHint')}</p>
            </div>
          </div>

      </div>
    </EntityFormDialog>
  );
}
